class WattshareStorefront {
  constructor() {
    this.bindMenu();
    this.bindCollectionFilters();
    this.bindCollectionSort();
    this.bindProductGallery();
    this.bindProductForm();
    this.bindTabs();
    this.bindCompatibilityGuidance();
  }

  bindMenu() {
    const toggle = document.querySelector('[data-ws-menu-toggle]');
    const menu = document.querySelector('[data-ws-mobile-menu]');
    if (!toggle || !menu) return;

    const closeMenu = () => {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
    };

    toggle.addEventListener('click', () => {
      const willOpen = menu.hidden;
      menu.hidden = !willOpen;
      toggle.setAttribute('aria-expanded', String(willOpen));
      toggle.setAttribute('aria-label', willOpen ? 'Close menu' : 'Open menu');
    });

    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', closeMenu));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeMenu();
    });
  }

  bindCollectionFilters() {
    const collection = document.querySelector('[data-ws-collection]');
    if (!collection) return;

    const cards = Array.from(collection.querySelectorAll('[data-ws-product-card]'));
    const chips = Array.from(collection.querySelectorAll('[data-ws-category-filter]'));
    const typeFilters = Array.from(collection.querySelectorAll('[data-ws-type-filter]'));
    const availabilityFilter = collection.querySelector('[data-ws-availability-filter]');
    const visibleCount = collection.querySelector('[data-ws-visible-count]');
    const emptyState = collection.querySelector('[data-ws-empty-state]');
    const reset = collection.querySelector('[data-ws-filter-reset]');
    let activeChip = 'all';

    const applyFilters = () => {
      const checkedTypes = typeFilters.filter((input) => input.checked).map((input) => input.value);
      const inStockOnly = Boolean(availabilityFilter?.checked);
      let count = 0;

      cards.forEach((card) => {
        const category = card.dataset.category;
        const categoryMatches = activeChip === 'all' || category === activeChip;
        const typeMatches = checkedTypes.length === 0 || checkedTypes.includes(category);
        const stockMatches = !inStockOnly || card.dataset.available === 'true';
        const visible = categoryMatches && typeMatches && stockMatches;
        card.hidden = !visible;
        if (visible) count += 1;
      });

      if (visibleCount) visibleCount.textContent = String(count);
      if (emptyState) emptyState.hidden = count !== 0;
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        activeChip = chip.dataset.wsCategoryFilter || 'all';
        chips.forEach((item) => {
          const active = item === chip;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        applyFilters();
      });
    });

    typeFilters.forEach((input) => input.addEventListener('change', applyFilters));
    availabilityFilter?.addEventListener('change', applyFilters);
    reset?.addEventListener('click', () => {
      activeChip = 'all';
      chips.forEach((chip) => {
        const active = chip.dataset.wsCategoryFilter === 'all';
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', String(active));
      });
      typeFilters.forEach((input) => {
        input.checked = false;
      });
      if (availabilityFilter) availabilityFilter.checked = false;
      applyFilters();
    });

    applyFilters();
  }

  bindCollectionSort() {
    const select = document.querySelector('[data-ws-sort-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  }

  bindProductGallery() {
    // Shopify's image_tag drops the empty data-ws-product-main-image attribute, so fall back
    // to the gallery image itself.
    const mainImage = document.querySelector('[data-ws-product-main-image], .ws-product-gallery__main img');
    const thumbnails = Array.from(document.querySelectorAll('[data-ws-product-thumb]'));
    if (!mainImage) return;

    const imagePath = (src) => (src || '').split('?')[0];
    this.showProductImage = (src, alt) => {
      if (!src) return;
      mainImage.removeAttribute('srcset');
      mainImage.src = src;
      mainImage.alt = alt || mainImage.alt;
      thumbnails.forEach((item) => {
        const active = imagePath(item.dataset.imageSrc) === imagePath(src);
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        this.showProductImage(thumbnail.dataset.imageSrc, thumbnail.dataset.imageAlt);
      });
    });
  }

  bindProductForm() {
    const product = document.querySelector('[data-ws-product]');
    if (!product) return;

    const quantity = product.querySelector('[data-ws-quantity]');
    product.querySelector('[data-ws-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Math.max(1, Number(quantity.value || 1) - 1));
    });
    product.querySelector('[data-ws-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = String(Math.max(1, Number(quantity.value || 1) + 1));
    });

    const variantSelect = product.querySelector('[data-ws-variant-select]');
    const price = product.querySelector('[data-ws-product-price]');
    const addButton = product.querySelector('[data-ws-add-to-cart]');
    variantSelect?.addEventListener('change', () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      const available = option.dataset.available === 'true';
      if (price) {
        const compare = option.dataset.comparePrice;
        price.replaceChildren();
        const current = document.createElement('span');
        current.textContent = option.dataset.price || '';
        price.append(current);
        if (compare) {
          const previous = document.createElement('s');
          previous.textContent = compare;
          price.append(previous);
        }
      }
      if (addButton) {
        addButton.disabled = !available;
        addButton.textContent = available ? 'Add to cart' : 'Sold out';
      }
      if (option.dataset.imageSrc) this.showProductImage?.(option.dataset.imageSrc, option.dataset.imageAlt);
    });
  }

  bindTabs() {
    document.querySelectorAll('[data-ws-tabs]').forEach((tabsRoot) => {
      const tabs = Array.from(tabsRoot.querySelectorAll('[role="tab"]'));
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          tabs.forEach((item) => {
            const active = item === tab;
            item.setAttribute('aria-selected', String(active));
            const panelId = item.getAttribute('aria-controls');
            const panel = panelId ? document.getElementById(panelId) : null;
            if (panel) panel.hidden = !active;
          });
        });
      });
    });
  }

  bindCompatibilityGuidance() {
    const root = document.querySelector('[data-ws-compatibility]');
    if (!root) return;
    const vehicle = root.querySelector('[data-ws-vehicle-type]');
    const inlet = root.querySelector('[data-ws-vehicle-inlet]');
    const source = root.querySelector('[data-ws-power-source]');
    const result = root.querySelector('[data-ws-guidance-result]');
    const button = root.querySelector('[data-ws-show-guidance]');
    if (!vehicle || !inlet || !source || !result || !button) return;

    button.addEventListener('click', () => {
      if (!vehicle.value || !inlet.value || !source.value) {
        result.textContent = 'Choose all three options so we can show the relevant checks.';
        result.hidden = false;
        return;
      }

      let guidance = 'Confirm the product connector and input rating against your vehicle handbook before ordering.';
      if (source.value === '10a') {
        guidance = 'For a standard 10A outlet, use charging equipment specifically rated for that outlet and your vehicle. Avoid unsuitable extension leads or unapproved adaptors.';
      } else if (source.value === '32a') {
        guidance = 'For a dedicated 32A supply, match the charger input rating and connector. Any new fixed outlet or electrical work must be completed by a qualified electrician.';
      } else if (source.value === 'type2') {
        guidance = 'For a Type 2 AC charging point, confirm the vehicle inlet, cable connector, phase and current rating. Charging is limited by the lowest supported component.';
      } else if (source.value === 'v2l') {
        guidance = 'For V2L, confirm that the exact vehicle model supports vehicle-to-load operation and use only a compatible adaptor within the manufacturer-specified output limits.';
      }

      if (inlet.value === 'other' || source.value === 'unsure' || vehicle.value === 'unsure') {
        guidance += ' Because one selection is uncertain, send Wattshare a clear photo of the vehicle connection and the intended setup before purchase.';
      }

      result.innerHTML = `<strong>Relevant checks</strong><p>${guidance}</p><p><a href="/collections/all">Browse current products</a> or <a href="mailto:support@wattshare.com.au?subject=Compatibility%20guidance">ask Wattshare for help</a>.</p>`;
      result.hidden = false;
      result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new WattshareStorefront());
} else {
  new WattshareStorefront();
}

